import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import api from "@/app/api";
import { UUID } from "crypto";
import type { PreOrder, PreOrderUpdate } from "@/app/types";
import type { PreOrder as OfflinePreOrder } from "@stockline/shared/types/orders/pre-order";
import {
  usePreOrderMutations,
  usePreOrderModifyBatch,
  type PreOrderFlowChange,
  type ModifyBatchInput,
} from "@stockline/web";
import { toast } from "sonner";
import { track } from "@/lib/analytics";
import { v2Api } from "@/lib/v2-api";
import { preOrderKeys } from "./keys";

/**
 * Translate a legacy snake_case `PreOrderUpdate` payload into the offline-first
 * camelCase patch shape expected by the V2 sync push endpoint.
 */
export function legacyToOfflinePatch(
  data: PreOrderUpdate,
): Partial<OfflinePreOrder> {
  const out: Partial<OfflinePreOrder> = {};
  if (data.status !== undefined) out.status = data.status;
  if (data.type !== undefined) out.type = data.type;
  if (data.partner_id !== undefined) out.partnerId = data.partner_id ?? null;
  if (data.shipping_address_id !== undefined)
    out.shippingAddressId = data.shipping_address_id ?? null;
  if (data.location_id !== undefined) out.locationId = data.location_id ?? null;
  if (data.group_id !== undefined) out.groupId = data.group_id ?? null;
  if (data.route_id !== undefined) out.routeId = data.route_id ?? null;
  if (data.employee_id !== undefined) out.employeeId = data.employee_id ?? null;
  if (data.pre_order_number !== undefined)
    out.preOrderNumber = data.pre_order_number ?? null;
  if (data.customer_po_number !== undefined)
    out.customerPoNumber = data.customer_po_number ?? null;
  if (data.comment !== undefined) out.comment = data.comment ?? null;
  if (data.delivery_date !== undefined)
    out.deliveryDate = data.delivery_date ?? null;
  if (data.order_date !== undefined) out.orderDate = data.order_date ?? null;
  if (data.pre_order_metadata !== undefined)
    out.preOrderMetadata =
      (data.pre_order_metadata as Record<string, unknown> | null) ?? null;
  return out;
}

// ---------------------------------------------------------------------------
// useUpdatePreOrder
// ---------------------------------------------------------------------------

/**
 * Hook to update a pre-order — offline-first.
 *
 * Writes the patch directly to Dexie via `usePreOrderMutations`, which:
 *   1. updates the offline `preOrders` row immediately (so `useLiveQuery`
 *      driven views — recap, detail — re-render in this tab without waiting
 *      for the network);
 *   2. enqueues a sync change for the V2 push endpoint;
 *   3. fires the V2 push, which emits `pre-order.updated` on the server
 *      event bus and fans out to every other connected client via SSE.
 *
 * Replaces the legacy `PUT /api/pre-orders/:id` path. The legacy endpoint
 * never touched Dexie, so recap (which reads Dexie via `useLiveQuery`) saw
 * neither the local edit nor the cross-tab fan-out — header changes
 * appeared to vanish locally even though the row landed in Postgres.
 *
 * Caller-visible interface (`mutate`, `mutateAsync`, `isPending`, per-call
 * `onSuccess`/`onError`) is unchanged. The synthetic return value is the
 * legacy snake_case shape patched onto the prior cached row, kept around
 * only because a few callsites' per-call `onSuccess` still inspect it; the
 * Dexie write is the source of truth for downstream views.
 */
export function useUpdatePreOrder() {
  const queryClient = useQueryClient();
  const { updatePreOrder } = usePreOrderMutations();

  return useMutation({
    mutationFn: async ({ id, data }: { id: UUID; data: PreOrderUpdate }) => {
      const patch = legacyToOfflinePatch(data);
      await updatePreOrder(id as string, patch);

      const cached = queryClient.getQueryData<PreOrder>(
        preOrderKeys.detail(id),
      );
      // Patch onto the cached row (when available) for callers whose per-call
      // `onSuccess` still inspects the result. Cast away null/undefined skew
      // between PreOrderUpdate (`null` for clears) and PreOrder (`undefined`)
      // — Dexie is the canonical store and downstream views read from it.
      return {
        ...(cached ?? { id }),
        ...data,
      } as PreOrder;
    },
  });
}

// ---------------------------------------------------------------------------
// useConfirmPreOrder — async-confirm v1
// ---------------------------------------------------------------------------

/**
 * Detect a 409 response on the V2 push-batch endpoint and read the
 * `error` discriminator from the body. V2 returns 409 for two distinct
 * cases:
 *
 *   - `"export_in_progress"`     — another export attempt is in-flight
 *                                  within the TTL. Wait for readback.
 *   - `"export_already_succeeded"`— the row was already successfully
 *                                  exported (reference on file) and
 *                                  nothing has been edited since. Show
 *                                  the existing ORNO instead of retrying.
 *
 * `pushBatch` re-wraps the underlying `HttpError` (which carries
 * `status` + `body`) into a plain `Error` whose `message` is the
 * response status line — the original is on `cause`. Walk both to find
 * the body, then read `body.error`.
 */
interface FortyNineDetails {
  kind: "export_in_progress" | "export_already_succeeded";
  reference?: string;
  preOrderId?: string;
}

function detectExport409(err: unknown): FortyNineDetails | null {
  if (!err || typeof err !== "object") return null;
  const candidates: unknown[] = [err];
  if ("cause" in err && (err as { cause: unknown }).cause) {
    candidates.push((err as { cause: unknown }).cause);
  }
  for (const c of candidates) {
    if (c && typeof c === "object" && "status" in c) {
      const status = (c as { status: unknown }).status;
      if (status !== 409) continue;
      const body = (c as { body?: unknown }).body;
      if (body && typeof body === "object") {
        const errorCode = (body as { error?: unknown }).error;
        if (errorCode === "export_already_succeeded") {
          return {
            kind: "export_already_succeeded",
            reference: (body as { reference?: string }).reference,
            preOrderId: (body as { preOrderId?: string }).preOrderId,
          };
        }
        if (errorCode === "export_in_progress") {
          return { kind: "export_in_progress" };
        }
      }
      // Status 409 with no parsable body — assume the older
      // `export_in_progress` shape for backward compat.
      return { kind: "export_in_progress" };
    }
  }
  // Last-resort: scan the message for the HTTP status prefix.
  if (err instanceof Error && /^HTTP 409/i.test(err.message)) {
    return { kind: "export_in_progress" };
  }
  return null;
}

// Back-compat wrapper — existing call sites check for ANY 409. Both
// kinds count as "lock held" from their perspective (don't retry).
function isExportLockHeldError(err: unknown): boolean {
  return detectExport409(err) !== null;
}

// New: callers that want to render "already exported as ORNO X" rather
// than "still exporting" check this discriminator.
function getExportAlreadySucceeded(
  err: unknown,
): { reference?: string; preOrderId?: string } | null {
  const d = detectExport409(err);
  if (d?.kind !== "export_already_succeeded") return null;
  return { reference: d.reference, preOrderId: d.preOrderId };
}

/**
 * Confirm a pre-order via the async-confirm v1 pipeline.
 *
 * Fires V2 `POST /pre-orders/:id/push-batch` with `intent: "confirm"`. The
 * server applies the (possibly empty) diff, stamps `metadata.export.status =
 * in_progress`, and emits `pre-order.confirm-requested` so erp-integrations
 * picks the order up asynchronously. SSE drives the FE through "Confirming…"
 * → "Confirmed #ORNO" without any further round-trips here.
 *
 * Replaces the synchronous `PUT /pre-orders/:id/status` legacy path that
 * blocked the FE for the full ~23s M3 round-trip. We deliberately do NOT
 * optimistically patch the TanStack cache — Dexie + SyncEngine fan-out the
 * server-stamped row, and the recap reads from Dexie via `useLiveQuery`.
 *
 * Failure modes:
 *  - 409 `export_in_progress` (the order is already mid-export) → toast and
 *    swallow. Don't retry; the in-flight readback will land first.
 *  - Any other error → bubble through TanStack `error` so the caller can
 *    show the per-order failure (e.g. `extractErpErrorMessage`).
 */
export function useConfirmPreOrder() {
  const modifyBatch = usePreOrderModifyBatch();
  // Per-id single-flight guard. Lives in a ref so it survives re-renders
  // without resetting; the set is drained in `onSettled` so subsequent
  // confirms for the same PO succeed once the current call resolves.
  //
  // Why: this hook exposes `mutateAsync` directly and is consumed by
  // multiple call sites (the recap action bar's confirm transition,
  // IllOrdersPopover Retry, the m3-locked retry path, future callers).
  // Only the action bar gates at the React level. Without
  // a guard at the hook itself, two rapid clicks on any call site fire
  // two `pushBatch` POSTs for the same PO id — V2 catches the second
  // with a 409 but only after the first POST has already enqueued
  // `pre-order.confirm-requested`, which is the exact double-fire path
  // that produced 6 duplicate M3 orders before commit 1a30d5d3f.
  const inFlightIds = useRef<Set<string>>(new Set());

  return useMutation({
    mutationFn: async (input: {
      preOrderId: string;
      flowChanges?: ModifyBatchInput["flowChanges"];
      preOrderUpdate?: ModifyBatchInput["preOrderUpdate"];
    }) => {
      // Reject the second concurrent call for the same PO with a typed
      // sentinel. Callers (recap action bar, retry-failed-export)
      // already swallow `code === "export_in_progress"`; we reuse the
      // same code so the existing branches keep working without a toast
      // for the rejection. The original `export_in_progress` is the 409
      // lock-held response from V2 — both mean "another in-flight call
      // for this id is already running", which is the contract the
      // callers want.
      if (inFlightIds.current.has(input.preOrderId)) {
        throw Object.assign(new Error("already_in_flight"), {
          code: "already_in_flight",
        });
      }
      inFlightIds.current.add(input.preOrderId);
      try {
        return await modifyBatch.pushBatch({
          preOrderId: input.preOrderId,
          flowChanges: input.flowChanges ?? [],
          preOrderUpdate: input.preOrderUpdate,
          intent: "confirm",
        });
      } catch (err) {
        const alreadyDone = getExportAlreadySucceeded(err);
        if (alreadyDone) {
          // The row was already successfully exported (reference on file)
          // and nothing has been edited since. Don't fire another export;
          // surface the existing ORNO so the user understands the state.
          // Same swallow pattern as the lock-held branch — callers
          // already check `code` to decide whether to render the failure.
          toast.info(`Already exported as ${alreadyDone.reference ?? "—"}`, {
            description:
              "This order is already in M3. Edit a field to enable a new export.",
          });
          throw Object.assign(new Error("export_already_succeeded"), {
            code: "export_already_succeeded",
            reference: alreadyDone.reference,
            preOrderId: alreadyDone.preOrderId,
          });
        }
        if (isExportLockHeldError(err)) {
          // Lock held: another export attempt for this PO is still in
          // flight on the server. The in-flight readback will land via SSE
          // — don't retry from the FE.
          toast.error("already exporting — wait for current attempt to finish");
          // Surface a sentinel so the caller can branch on lock-held vs a
          // real error without re-sniffing the message.
          throw Object.assign(new Error("export_in_progress"), {
            code: "export_in_progress",
          });
        }
        throw err;
      } finally {
        inFlightIds.current.delete(input.preOrderId);
      }
    },
    // No analytics here: the confirm is captured authoritatively server-side as
    // `order_confirmed` (event bus, carries tons/euros/source), and the actual
    // ERP export success as `order_exported`. A client "export requested" event
    // would just duplicate `order_confirmed` for the same gesture.
  });
}

// Re-export the lock-held detector so callers (recap mutators, the
// IllOrdersPopover retry path) can branch on it without re-sniffing the
// raw error shape. Kept private to the hook before — promoted to module
// API the moment the second consumer needed it.
export { isExportLockHeldError, getExportAlreadySucceeded };

/**
 * User-facing Retry button — forces M3 to mirror local state exactly.
 *
 * Calls V2's dedicated `/retry-export` endpoint, which publishes
 * `pre-order.confirm-requested` with `mode: "full_replace"`. The
 * ConfirmConsumer routes that to `_do_full_replace`: enumerate M3's
 * current PONRs, build a modify payload that deletes every M3 line
 * and re-adds every local flow. ORNO is preserved.
 *
 * Differs from `useConfirmPreOrder` in that:
 * - Bypasses V2's already-success gate (retry IS the explicit
 *   override saying "push to M3 even though nothing changed locally").
 * - Does NOT carry a flow-diff payload — the server fetches local
 *   state itself and pushes it whole to M3.
 * - Fires only on rows with a `metadata.export.reference` (you can't
 *   "retry" something that was never exported — use Confirm instead).
 *
 * Failure modes:
 * - 409 `cannot_retry_no_reference` — row never exported. Toast +
 *   surface a sentinel so the caller can swap to the Confirm path.
 * - 409 `export_in_progress` — another export in-flight. Same sentinel
 *   as `useConfirmPreOrder` so existing call-site swallows work.
 * - Per-row single-flight via `retryInFlightIds` ref.
 */
export function useRetryExport() {
  const retryInFlightIds = useRef<Set<string>>(new Set());

  return useMutation({
    mutationFn: async (input: { preOrderId: string }) => {
      if (retryInFlightIds.current.has(input.preOrderId)) {
        throw Object.assign(new Error("already_in_flight"), {
          code: "already_in_flight",
        });
      }
      retryInFlightIds.current.add(input.preOrderId);
      try {
        return await v2Api.preOrders.retryExport(input.preOrderId);
      } catch (err) {
        // Check the SPECIFIC retry-only sentinel BEFORE the generic
        // 409 classifier — `detectExport409` would otherwise tag it
        // as `export_in_progress` and we'd surface the wrong toast
        // ("already exporting" instead of "use Confirm instead").
        // Both V2 errors land as 409; the body's `error` discriminator
        // is the only way to tell them apart.
        const errBody = extractErrorBody(err);
        if (errBody?.error === "cannot_retry_no_reference") {
          toast.error(
            "This order has never been exported — use Confirm instead",
          );
          throw Object.assign(new Error("cannot_retry_no_reference"), {
            code: "cannot_retry_no_reference",
          });
        }
        // Body-aware export-in-progress detection, same shape as
        // useConfirmPreOrder. Runs AFTER the specific-sentinel check
        // above so we never mis-classify cannot_retry as in_progress.
        const detected = detectExport409(err);
        if (detected?.kind === "export_in_progress") {
          toast.error("already exporting — wait for current attempt to finish");
          throw Object.assign(new Error("export_in_progress"), {
            code: "export_in_progress",
          });
        }
        throw err;
      } finally {
        retryInFlightIds.current.delete(input.preOrderId);
      }
    },
    onSuccess: (_data, input) => {
      track("export_retried", { pre_order_id: input.preOrderId });
    },
  });
}

/**
 * Walk an `HttpError`-shaped rejection (and its `cause` chain) and
 * return the response body if it looks like a structured JSON error
 * payload. Used by `useRetryExport` to distinguish the dedicated
 * `cannot_retry_no_reference` 409 from the generic `export_in_progress`.
 */
function extractErrorBody(
  err: unknown,
): { error?: string; message?: string } | null {
  if (!err || typeof err !== "object") return null;
  const candidates: unknown[] = [err];
  if ("cause" in err && (err as { cause: unknown }).cause) {
    candidates.push((err as { cause: unknown }).cause);
  }
  for (const c of candidates) {
    if (c && typeof c === "object" && "body" in c) {
      const body = (c as { body: unknown }).body;
      if (body && typeof body === "object") {
        return body as { error?: string; message?: string };
      }
    }
  }
  return null;
}

/**
 * Hook to update pre-order status
 */
export function useUpdatePreOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: UUID; status: number }) =>
      api.preOrders.updateStatus(id, status),
    onSuccess: (_data, { id }) => {
      // Invalidate only the specific detail + recap queries (not all pre-order queries)
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

/**
 * Hook to batch update multiple pre-order statuses with optimistic updates
 */
export function useBatchUpdatePreOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      updates,
    }: {
      updates: Array<{ id: UUID; status: number }>;
    }) => {
      // Execute all updates in parallel
      const results = await Promise.all(
        updates.map(({ id, status }) => api.preOrders.updateStatus(id, status)),
      );
      return results;
    },

    onMutate: async ({ updates }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: preOrderKeys.all });

      // Snapshot the previous values
      const previousRecapQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: [...preOrderKeys.all, "recap"] })
        .map((query) => ({
          queryKey: query.queryKey,
          data: query.state.data,
        }));

      // Optimistically update all recap queries
      previousRecapQueries.forEach(({ queryKey, data }) => {
        if (Array.isArray(data)) {
          const optimisticData = data.map((preOrder: any) => {
            // Find if this pre-order should be updated
            const update = updates.find((u) => u.id === preOrder.id);
            if (update) {
              // Determine new flow statuses based on pre-order status
              let updatedFlows = preOrder.flows;
              if (update.status === 1) {
                // Confirming: set all non-cancelled flows to confirmed
                updatedFlows = preOrder.flows?.map((flow: any) => ({
                  ...flow,
                  status: flow.status === 3 ? 3 : 1, // Keep cancelled (3), else confirmed (1)
                }));
              } else if (update.status === 0) {
                // Pending: set all confirmed flows back to pending
                updatedFlows = preOrder.flows?.map((flow: any) => ({
                  ...flow,
                  status: flow.status === 1 ? 0 : flow.status, // Confirmed (1) -> pending (0)
                }));
              }

              return {
                ...preOrder,
                status: update.status,
                flows: updatedFlows,
              };
            }
            return preOrder;
          });

          queryClient.setQueryData(queryKey, optimisticData);
        }
      });

      // Return context for rollback
      return { previousRecapQueries };
    },

    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousRecapQueries) {
        context.previousRecapQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSuccess: () => {
      // Mark recap queries stale so they refetch in the background
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

/**
 * Apply the same delivery_date to N pre-orders in one V2 batch call.
 *
 * Server (`apps/api/src/domains/orders/pre-order/service.ts`) wraps every
 * write in a single transaction and silently skips rows the user can't
 * access (owner / manager / group). The hook returns `{ updated, updatedIds,
 * skippedIds }` so callers can toast partial-success accurately.
 *
 * Optimistically patches `delivery_date` on all `ids` in every cached recap
 * query and rolls back on error. Rolls forward on success: the per-row write
 * lands in `updatedIds`, and rows in `skippedIds` snap back to their
 * pre-mutation value via the `recap` invalidation.
 */
export function useBatchUpdatePreOrderDeliveryDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ids,
      deliveryDate,
    }: {
      ids: UUID[];
      deliveryDate: string | null;
    }) =>
      v2Api.preOrders.batchUpdateDeliveryDate({
        ids: ids as unknown as string[],
        deliveryDate,
      }),

    onMutate: async ({ ids, deliveryDate }) => {
      // Scope cancellation to the recap queries we actually snapshot and
      // patch — cancelling all pre-order queries would silently kill
      // in-flight detail/list refetches that we never restore on rollback.
      const recapKey = [...preOrderKeys.all, "recap"];
      await queryClient.cancelQueries({ queryKey: recapKey });

      const idSet = new Set(ids as unknown as string[]);

      const previousRecapQueries = queryClient
        .getQueryCache()
        .findAll({ queryKey: recapKey })
        .map((query) => ({
          queryKey: query.queryKey,
          data: query.state.data,
        }));

      previousRecapQueries.forEach(({ queryKey, data }) => {
        if (Array.isArray(data)) {
          const next = data.map((preOrder: any) =>
            idSet.has(preOrder.id)
              ? { ...preOrder, delivery_date: deliveryDate }
              : preOrder,
          );
          queryClient.setQueryData(queryKey, next);
        }
      });

      return { previousRecapQueries };
    },

    onError: (_err, _variables, context) => {
      if (context?.previousRecapQueries) {
        context.previousRecapQueries.forEach(({ queryKey, data }) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },

    onSuccess: () => {
      // Recap queries refresh in the background; skipped ids snap back to
      // their server value via the refetch.
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

/**
 * Hook to update a pre-order line
 */
export function useUpdatePreOrderLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preOrderId,
      lineId,
      data,
    }: {
      preOrderId: UUID;
      lineId: UUID;
      data: any;
    }) =>
      api.preOrders.updateLine(preOrderId, lineId, {
        line_id: lineId,
        ...data,
      }),
    onSuccess: (_data, { preOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(preOrderId),
      });
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

/**
 * Hook to confirm a pre-order line
 */
export function useConfirmPreOrderLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preOrderId,
      lineId,
      partnerId,
    }: {
      preOrderId: UUID;
      lineId: UUID;
      partnerId?: UUID;
    }) => api.preOrders.confirmLine(preOrderId, lineId, partnerId),
    onSuccess: (_data, { preOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(preOrderId),
      });
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

/**
 * Hook to cancel a pre-order line
 */
export function useCancelPreOrderLine() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      preOrderId,
      lineId,
      reason,
    }: {
      preOrderId: UUID;
      lineId: UUID;
      reason?: string;
    }) => api.preOrders.cancelLine(preOrderId, lineId, { reason } as any),
    onSuccess: (_data, { preOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(preOrderId),
      });
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

/**
 * Hook to bulk confirm lines
 */
export function useBulkConfirmLines() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { line_ids: UUID[]; partner_id?: UUID }) =>
      api.preOrders.bulkConfirmLines(data as any),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
    },
  });
}

// Shared optimistic-cache helpers for mutations that touch the recap, list,
// and email caches together (delete, cancel). Centralising them keeps the
// snapshot/restore boilerplate from drifting between hooks.
type QuerySnapshot = { queryKey: readonly unknown[]; data: unknown };
type PreOrderListSnapshots = {
  previousRecapQueries: QuerySnapshot[];
  previousListQueries: QuerySnapshot[];
  previousEmailQueries: QuerySnapshot[];
};

async function cancelPreOrderListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await queryClient.cancelQueries({
    queryKey: [...preOrderKeys.all, "recap"],
  });
  await queryClient.cancelQueries({ queryKey: preOrderKeys.lists() });
  await queryClient.cancelQueries({ queryKey: preOrderKeys.byEmail() });
}

function snapshotPreOrderListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
): PreOrderListSnapshots {
  const snap = (queryKey: readonly unknown[]): QuerySnapshot[] =>
    queryClient
      .getQueryCache()
      .findAll({ queryKey })
      .map((query) => ({ queryKey: query.queryKey, data: query.state.data }));
  return {
    previousRecapQueries: snap([...preOrderKeys.all, "recap"]),
    previousListQueries: snap(preOrderKeys.lists()),
    previousEmailQueries: snap(preOrderKeys.byEmail()),
  };
}

function restorePreOrderListCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  snapshots: Partial<PreOrderListSnapshots> | undefined,
) {
  if (!snapshots) return;
  const restore = (entries: QuerySnapshot[] | undefined) => {
    entries?.forEach(({ queryKey, data }) => {
      queryClient.setQueryData(queryKey, data);
    });
  };
  restore(snapshots.previousRecapQueries);
  restore(snapshots.previousListQueries);
  restore(snapshots.previousEmailQueries);
}

/**
 * Hook to delete a pre-order — offline-first.
 *
 * `mutationFn` writes a Dexie tombstone via `usePreOrderMutations.deletePreOrder`
 * (`apps/web/src/app/orders/hooks/usePreOrderMutations.ts`), which:
 *   1. soft-deletes the offline `preOrders` row (sets `deletedAt`) so recap's
 *      `useLiveQuery` re-renders within one frame;
 *   2. enqueues an `op: delete` push on the V2 change queue (durable across
 *      reloads, retries on reconnect);
 *   3. fires `engine.pushChanges("pre-orders")` fire-and-forget. The server
 *      stamps `syncId`/`updatedAt` and SSE fans the row out to peer clients.
 *
 * Replaces the legacy `DELETE /api/pre-orders/{id}` path. Three problems
 * drove this migration:
 *   1. Recap reads from Dexie via `useLiveQuery` in `offlineShim`; the
 *      legacy `onMutate` rewrote TanStack caches that recap doesn't read,
 *      so the optimistic block was a no-op for the user-facing wait.
 *   2. FastAPI's per-row delete is 700-1000ms; six in parallel = ~1s of
 *      unchanged UI.
 *   3. Legacy SSE deletes ship with a stale `prevSyncId` that triggers
 *      gap-driven resyncs in `SyncEngine`, swallowing 5/6 events in a
 *      batch. Recap converges only after the resync catches up.
 *
 * The TanStack snapshot/rewrite in `onMutate` is kept as defense for the
 * paginated list and email views (which do NOT read Dexie). Recap is
 * Dexie-driven and updates from the `offlineDelete` write inside
 * `mutationFn`; TanStack ordering is `onMutate` → `mutationFn` → `onSuccess`
 * (or `onError`), so the Dexie write happens a few ms after the TanStack
 * rewrite, but both land before the user can react.
 */
export function useDeletePreOrder() {
  const queryClient = useQueryClient();
  const { deletePreOrder: offlineDelete, undeletePreOrder: offlineUndelete } =
    usePreOrderMutations();

  return useMutation({
    mutationFn: (preOrderId: string) => offlineDelete(preOrderId),

    onMutate: async (preOrderId) => {
      await cancelPreOrderListCaches(queryClient);
      await queryClient.cancelQueries({
        queryKey: preOrderKeys.detail(preOrderId as UUID),
      });
      const {
        previousRecapQueries,
        previousListQueries,
        previousEmailQueries,
      } = snapshotPreOrderListCaches(queryClient);

      const previousDetail = queryClient.getQueryData<any>(
        preOrderKeys.detail(preOrderId as UUID),
      );

      const tombstoneTimestamp = new Date().toISOString();

      // Mark the pre-order as soft-deleted in recap caches. The recap
      // filtering hook hides deleted rows by default and surfaces them when
      // "Show deleted orders" is on, so toggling the switch reveals the
      // just-deleted pre-order without a refetch.
      previousRecapQueries.forEach(({ queryKey, data }) => {
        if (Array.isArray(data)) {
          const updatedData = data.map((preOrder: any) =>
            preOrder.id === preOrderId
              ? { ...preOrder, deleted_at: tombstoneTimestamp }
              : preOrder,
          );
          queryClient.setQueryData(queryKey, updatedData);
        }
      });

      // Tombstone the detail cache too so the chat-panel PreOrderContent
      // (and any open detail view) re-renders with the deleted styling
      // immediately, without waiting for the server round-trip.
      if (previousDetail) {
        queryClient.setQueryData(preOrderKeys.detail(preOrderId as UUID), {
          ...previousDetail,
          deleted_at: tombstoneTimestamp,
        });
      }

      // Remove from all list queries
      previousListQueries.forEach(({ queryKey, data }) => {
        if (data && typeof data === "object" && "items" in data) {
          const listData = data as { items: any[] };
          const updatedItems = listData.items.filter(
            (preOrder: any) => preOrder.id !== preOrderId,
          );
          queryClient.setQueryData(queryKey, {
            ...listData,
            items: updatedItems,
          });
        }
      });

      // Remove from all email queries (paginated lists with items)
      previousEmailQueries.forEach(({ queryKey, data }) => {
        if (data && typeof data === "object" && "items" in data) {
          const listData = data as { items: any[]; total: number };
          const updatedItems = listData.items.filter(
            (preOrder: any) => preOrder.id !== preOrderId,
          );
          queryClient.setQueryData(queryKey, {
            ...listData,
            items: updatedItems,
            total: updatedItems.length,
          });
        }
      });

      return {
        previousRecapQueries,
        previousListQueries,
        previousEmailQueries,
        previousDetail,
        preOrderId,
      };
    },

    onError: async (_err, preOrderId, context) => {
      // Clear the Dexie tombstone first so recap re-renders the row, then
      // restore the TanStack caches for non-Dexie consumers. Swallow
      // undelete failures — if Dexie is gone the row will be re-seeded by
      // the next sync.
      await offlineUndelete(preOrderId).catch(() => {});
      restorePreOrderListCaches(queryClient, context);
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          preOrderKeys.detail(preOrderId as UUID),
          context.previousDetail,
        );
      }
    },

    onSuccess: (_data, preOrderId) => {
      // Mark the detail cache as soft-deleted instead of evicting it, so
      // the show-deleted recap view (and any open detail screen) can still
      // read it without a refetch. Recap itself is Dexie-driven; this is
      // defense for legacy detail consumers.
      const existing = queryClient.getQueryData<any>(
        preOrderKeys.detail(preOrderId as UUID),
      );
      if (existing) {
        queryClient.setQueryData(preOrderKeys.detail(preOrderId as UUID), {
          ...existing,
          deleted_at: existing.deleted_at ?? new Date().toISOString(),
        });
      }
    },
  });
}

/**
 * Hook to cancel a pre-order. The order stays visible (status=CANCELLED) and
 * all its flows are cascaded to CANCELLED.
 *
 * Optimistic — mirrors useDeletePreOrder/useUpdatePreOrderStatus: snapshot
 * recap + list + email + detail caches, mutate them in place, roll back on
 * error. The server response replaces the optimistic data on success so any
 * server-stamped fields (audit ids, refreshed updated_at) reach the cache.
 */
export function useCancelPreOrder() {
  const queryClient = useQueryClient();

  const PRE_ORDER_STATUS_CANCELLED = 6;
  const FLOW_STATUS_CANCELLED = 3;

  const applyCancelToPreOrder = (po: any) => {
    if (po?.id === undefined) return po;
    const cancelledFlows = Array.isArray(po.flows)
      ? po.flows.map((f: any) =>
          f.status === FLOW_STATUS_CANCELLED
            ? f
            : { ...f, status: FLOW_STATUS_CANCELLED },
        )
      : po.flows;
    return {
      ...po,
      status: PRE_ORDER_STATUS_CANCELLED,
      is_cancelled: true,
      flows: cancelledFlows,
    };
  };

  return useMutation({
    mutationFn: ({
      preOrderId,
      reason,
    }: {
      preOrderId: string;
      reason?: string | null;
    }) => api.preOrders.cancelPreOrder(preOrderId, reason ?? null),

    onMutate: async ({ preOrderId }) => {
      await cancelPreOrderListCaches(queryClient);
      await queryClient.cancelQueries({
        queryKey: preOrderKeys.detail(preOrderId as UUID),
      });

      const {
        previousRecapQueries,
        previousListQueries,
        previousEmailQueries,
      } = snapshotPreOrderListCaches(queryClient);

      const previousDetail = queryClient.getQueryData(
        preOrderKeys.detail(preOrderId as UUID),
      );

      previousRecapQueries.forEach(({ queryKey, data }) => {
        if (Array.isArray(data)) {
          queryClient.setQueryData(
            queryKey,
            data.map((po: any) =>
              po.id === preOrderId ? applyCancelToPreOrder(po) : po,
            ),
          );
        }
      });

      previousListQueries.forEach(({ queryKey, data }) => {
        if (data && typeof data === "object" && "items" in data) {
          const listData = data as { items: any[] };
          queryClient.setQueryData(queryKey, {
            ...listData,
            items: listData.items.map((po: any) =>
              po.id === preOrderId ? applyCancelToPreOrder(po) : po,
            ),
          });
        }
      });

      previousEmailQueries.forEach(({ queryKey, data }) => {
        if (data && typeof data === "object" && "items" in data) {
          const listData = data as { items: any[]; total: number };
          queryClient.setQueryData(queryKey, {
            ...listData,
            items: listData.items.map((po: any) =>
              po.id === preOrderId ? applyCancelToPreOrder(po) : po,
            ),
          });
        }
      });

      if (previousDetail) {
        queryClient.setQueryData(
          preOrderKeys.detail(preOrderId as UUID),
          applyCancelToPreOrder(previousDetail),
        );
      }

      return {
        previousRecapQueries,
        previousListQueries,
        previousEmailQueries,
        previousDetail,
        preOrderId,
      };
    },

    onError: (_err, _vars, context) => {
      restorePreOrderListCaches(queryClient, context);
      if (context?.previousDetail !== undefined && context?.preOrderId) {
        queryClient.setQueryData(
          preOrderKeys.detail(context.preOrderId as UUID),
          context.previousDetail,
        );
      }
    },

    onSuccess: (data, { preOrderId }) => {
      // Replace optimistic detail with server response (carries server-stamped
      // updated_at, audit-aware metadata, etc.).
      queryClient.setQueryData(preOrderKeys.detail(preOrderId as UUID), data);
    },

    // Mark recap/list/email queries stale so a background refetch reconciles
    // the optimistic cache with server truth. Without this, the persisted
    // (IndexedDB) cache can rehydrate a pre-cancel snapshot after a refresh
    // and never refetch while still inside the 60s staleTime window.
    onSettled: (_data, _err, { preOrderId }) => {
      queryClient.invalidateQueries({
        queryKey: [...preOrderKeys.all, "recap"],
      });
      queryClient.invalidateQueries({ queryKey: preOrderKeys.lists() });
      queryClient.invalidateQueries({ queryKey: preOrderKeys.byEmail() });
      queryClient.invalidateQueries({
        queryKey: preOrderKeys.detail(preOrderId as UUID),
      });
    },
  });
}
